/**
 * Ryan O'Dowd
 * 2018-08-19
 * © Copyright 2018 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useEffect,
  useState,
} from 'react';
import ActivityIndicator from '@components/common/ActivityIndicator';
import AlertMessages from '@containers/Auth/AlertMessages';
import Button from '@components/common/Button';
import Dialog from '@components/common/Dialog';
import {
  KeyboardAvoidingView,
} from 'react-native-keyboard-controller';
import Text from '@components/common/Text';
import TextInput from '@components/common/TextInput';
import {
  View,
} from 'react-native';
import fb from '@utils/firebase';
import getStyles from './styles';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const CustomAuthForm = (props: CustomAuthFormProps) => {
  const navigation = hooks.useNavigation();
  const styles = hooks.useStyles(getStyles);

  const [_authIsPending, setAuthIsPending] = useState(false);

  hooks.useBackButton(() => {
    navigation.goBack();
    return true;
  });

  const anonymousUserToLink = props.isLinking ? fb.auth.currentUser : null;
  const i18n = hooks.useI18n(props.localeId);

  const [_errorMessage, setErrorMessage] = useState('');
  const [_successMessage, setSuccessMessage] = useState(null);

  const [_isSubmitted, setIsSubmitted] = useState(false);
  const [_isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  const user = hooks.useTypedSelector((state) => state.oakUser);

  const [_firstNameText, setFirstNameText] = useState('');
  const [_lastNameText, setLastNameText] = useState('');
  const [_emailText, setEmailText] = useState('');
  const [_passwordText, setPasswordText] = useState('');
  const [_isEmailSet, setIsEmailSet] = useState(false);
  const [_isNameFormVisible, setIsNameFormVisible] = useState(props.authType === 'anonymous');
  const [_isPasswordResetButtonVisible, setIsPasswordResetButtonVisible] = useState(true);

  const _isSubmitDisabled = () => {
    if (_authIsPending) {
      return true;
    }

    if (props.authType === 'email') {
      if (!_emailText) {
        return true;
      }
      if (_isEmailSet) {
        return !_passwordText;
      }
      if (_isNameFormVisible) {
        return !_firstNameText || !_lastNameText;
      }
    }

    return false;
  };

  useEffect(() => {
    if (user && !anonymousUserToLink) {
      navigation.pop();
    }
  }, [user, anonymousUserToLink, navigation]);

  let submitButtonText = i18n('continueAsGuest');
  let instructionalText = i18n('dataNotSynced');
  if (props.authType === 'email') {
    submitButtonText = 'Next'; // @TODO: i18n
    instructionalText = 'Please enter your email address.'; // @TODO: i18n
    if (_isEmailSet) {
      if (_isNameFormVisible) {
        instructionalText = 'Please enter your name and set your new password.'; // @TODO: i18n
      } else if (anonymousUserToLink) {
        instructionalText = 'Please set your new password.'; // @TODO: i18n
      } else {
        instructionalText = 'Please enter your password.'; // @TODO: i18n
      }
    }
  }

  const _getDisplayName = (firstName: string, lastName: string) => {
    const displayName = `${firstName.trim()} ${lastName.trim()}`;
    return displayName.trim() || 'User';
  };

  return (
    <React.Fragment>
      <View style={styles.authFormContainerWrapper}>
        <KeyboardAvoidingView
          style={styles.authFormContainer}
          behavior='padding'
        >
          <Text style={styles.formText}>{instructionalText}</Text>
          <View style={styles.inputsWrapper}>
            {(props.authType === 'email' && !_isEmailSet)
              ? (
                <TextInput
                  autoCapitalize='none'
                  autoCorrect={false}
                  mode='outlined'
                  onChangeText={(emailText) => setEmailText(emailText.trim())}
                  value={_emailText}
                  placeholder={i18n('emailAddress')}
                  keyboardType='email-address'
                  disabled={_isEmailSet || _isSubmitted}
                />
              )
              : (
                <React.Fragment>
                  {/* @TODO: add mode='outlined' to these and remove most props */}
                  {_isNameFormVisible &&
                    <React.Fragment>
                      <TextInput
                        autoFocus={true}
                        autoCapitalize='words'
                        autoCorrect={false}
                        mode='outlined'
                        onChangeText={(value) => setFirstNameText(value)}
                        value={_firstNameText}
                        placeholder={i18n('firstName')}
                        disabled={_isSubmitted}
                      />
                      <TextInput
                        autoCapitalize='words'
                        autoCorrect={false}
                        mode='outlined'
                        onChangeText={(value) => setLastNameText(value)}
                        value={_lastNameText}
                        placeholder={i18n('lastName')}
                        disabled={_isSubmitted}
                      />
                    </React.Fragment>
                  }
                  {props.authType === 'email' &&
                    <React.Fragment>
                      <TextInput
                        autoCapitalize='none'
                        autoCorrect={false}
                        mode='outlined'
                        onChangeText={(passwordText) => setPasswordText(passwordText)}
                        value={_passwordText}
                        placeholder='Password'// @TODO: i18n
                        secureTextEntry={true}
                        disabled={_isSubmitted}
                      />
                      {_isPasswordResetButtonVisible &&
                        <Button
                          mode='text'
                          theme={styles.colors.white}
                          onPress={async () => {
                            await fb.sendPasswordResetEmail(fb.auth, _emailText);
                            setErrorMessage('Password reset email sent; please check your inbox.'); // @TODO: i18n
                          }}
                        >
                          {i18n('forgotPassword')}
                        </Button>
                      }
                    </React.Fragment>
                  }
                </React.Fragment>
              )
            }
          </View>
          <AlertMessages
            errorMessage={_errorMessage}
            successMessage={_successMessage}
          />
          <View style={styles.buttonLoadingWrapper}>
            <Button
              containerStyle={styles.submitButton}// @TODO: should this be style instead of containerStyle?
              mode='contained'
              disabled={_isSubmitDisabled()}
              theme={styles.colors.white}
              onPress={async () => {
                if (props.authType !== 'email') {
                  setIsConfirmDialogOpen(true);
                } else {
                  try {
                    const authMethodsForUser = await fb.fetchSignInMethodsForEmail(fb.auth, _emailText);
                    if (anonymousUserToLink && authMethodsForUser.length > 0) {
                      setErrorMessage('This email is already linked to another account.');
                      return;
                    }
                    if (authMethodsForUser.length === 0) {
                      if (_emailText && _passwordText) {
                        if (anonymousUserToLink) {
                          const credential = fb.EmailAuthProvider.credential(_emailText, _passwordText);
                          utilities.linkUserAndCredential(anonymousUserToLink, credential, navigation);
                          return;
                        } else {
                          const credential = await fb.createUserWithEmailAndPassword(fb.auth, _emailText, _passwordText);
                          const displayName = _getDisplayName(_firstNameText, _lastNameText);
                          utilities.updateDisplayName(credential.user, displayName);
                          fb.logEvent(fb.analytics, 'login', { // @TODO: abstract with other calls to robots
                            displayName,
                            email: _emailText,
                            authType: props.authType,
                          });
                        }
                      } else {
                        setIsEmailSet(true);
                        setIsNameFormVisible(!anonymousUserToLink);
                        setErrorMessage('');
                        return;
                      }
                    } else if (authMethodsForUser.includes('password')) {
                      if (_emailText && _passwordText) {
                        await fb.signInWithEmailAndPassword(fb.auth, _emailText, _passwordText);
                      } else {
                        setIsEmailSet(true);
                        setIsNameFormVisible(false);
                        setErrorMessage('');
                        setSuccessMessage('');
                        return;
                      }
                    } else if (authMethodsForUser.includes('emailLink')) {
                      setIsPasswordResetButtonVisible(false);
                      await fb.sendPasswordResetEmail(fb.auth, _emailText);
                      setSuccessMessage('Welcome back!  Please check your email to update your password before continuing.'); // @TODO: i18n
                      setErrorMessage('');
                      return;
                    } else {
                      setErrorMessage(i18n('authEmailAlreadyInUse'));
                      return;
                    }
                  } catch (error) {
                    let errorMessage = '';
                    switch (error.code) {
                      case 'auth/network-request-failed': {
                        errorMessage = i18n('networkError');
                        break;
                      }
                      case 'auth/account-exists-with-different-credential': {
                        errorMessage = i18n('authEmailAlreadyInUse');
                        break;
                      }
                      case 'auth/email-already-in-use': // Thrown if the email corresponding to the credential already exists among your users. When thrown while linking a credential to an existing user, an error.email and error.credential AuthCredential fields are also provided. You have to link the credential to the existing user with that email if you wish to continue signing in with that credential. To do so, call Auth#fetchProvidersForEmail, sign in to error.email via one of the providers returned and then User#linkWithCredential the original credential to that newly signed in user
                        errorMessage = 'An existing account is already associated with this email address.'; // @TODO: i18n (shared with utilities/common.ts)
                        break;
                      case 'auth/weak-password': {
                        errorMessage = 'Password must be at least 6 characters.'; // @TODO: i18n (shared with utilities/common.ts)
                        break;
                      }
                      case 'auth/invalid-email': {
                        errorMessage = i18n('invalidEmailError');
                        break;
                      }
                      case 'auth/wrong-password': {
                        errorMessage = 'Incorrect password; please try again.'; // @TODO: i18n
                        break;
                      }
                      case 'auth/argument-error':
                      case 'auth/operation-not-allowed':
                      case 'auth/missing-android-pkg-name':
                      case 'auth/missing-continue-uri':
                      case 'auth/missing-ios-bundle-id':
                      case 'auth/invalid-continue-uri':
                      case 'auth/unauthorized-continue-uri': {
                        errorMessage = i18n('unknownError');
                        break;
                      }
                      default: {
                        // user doesn't care about these errors
                        errorMessage = i18n('unknownError');
                        fb.recordError(fb.crashlytics, error);
                      }
                    }
                    setErrorMessage(errorMessage);
                    setIsSubmitted(false);
                    return;
                  }
                }
                setErrorMessage('');

                setIsSubmitted(true);
              }}
            >
              {submitButtonText}
            </Button>
            {_isSubmitted && <ActivityIndicator style={styles.loadingIndicator} animating={true} size='small' color={styles.colors.white} />}
          </View>
        </KeyboardAvoidingView>
      </View>
      {_isConfirmDialogOpen &&
        <Dialog
          visible={_isConfirmDialogOpen}
          onDismiss={() => {
            setIsConfirmDialogOpen(false);
            setIsSubmitted(false);
          }}
        >
          <Dialog.Title>{i18n('guestConfirmation')}</Dialog.Title>
          <Dialog.Content>
            <Text style={styles.guestConfirmationText}>
              {i18n('areYouSureGuest')}
            </Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button
              mode='text'
              onPress={() => {
                setIsSubmitted(false);
                setIsConfirmDialogOpen(false);
              }}
            >
              {i18n('takeMeBack')}
            </Button>
            <Button
              mode='contained'
              disabled={_authIsPending}
              onPress={async () => {
                setAuthIsPending(true);
                const credential = await fb.signInAnonymously(fb.auth);
                const displayName = _getDisplayName(_firstNameText, _lastNameText);
                utilities.updateDisplayName(credential.user, displayName);
                fb.logEvent(fb.analytics, 'login', { // @TODO: abstract with other calls to robots
                  displayName,
                  email: _emailText,
                  authType: props.authType,
                });
                setIsConfirmDialogOpen(false);
              }}
            >
              {i18n('continueAsGuest')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      }
    </React.Fragment>
  );
};

class CustomAuthFormProps {
  localeId: any;

  authType?: 'anonymous' | 'email' | undefined; // @TODO: anonymous is only allowed if not web (ternary must return value not type)
  isLinking?: boolean;
};

export default CustomAuthForm;
